// Artifact-backed selector for UGC video element plans.
//
// Before Phase 3, element plans lived only in Zustand (keyed by scriptAngle)
// and were persisted to localStorage. That worked for a single product per
// workspace, but cross-product leakage forced every reader to apply an
// `elementPlansProductId` clear-on-mismatch guard. Now that Phase 2A scopes
// every `ugc-video-plan` artifact by productId, the artifact rows are the
// source of truth: this hook filters by product (via the shared matcher) and
// returns the latest non-deprecated plan per videoIndex.
//
// `useArtifacts` already filters out is_deprecated rows, and the
// `ugc-video-plan` save endpoint supersedes prior plans for the same
// (workspace, productId, videoIndex), so the visible map is always the
// latest plan run.

import { useMemo } from "react";
import { useArtifacts, type ArtifactRecord } from "@/hooks/useArtifacts";
import { artifactMatchesProduct } from "./productArtifactScope";
import type {
  CaptionSpec,
  OverlayPlanItem,
  TransitionSpec,
  UgcAvatarPersona,
  UgcVideoElementPlan,
  VideoScript,
} from "../components/useAdsResearchStore";

/** Persisted shape stored at `Artifact.metadata.ugcVideoPlan`. Mirrors
 *  core/src/domains/growth/services/ugcPlanStore.ts:UgcVideoPlanRecord. */
export interface UgcVideoPlan {
  videoIndex: number;
  elementPlan: UgcVideoElementPlan;
  script: VideoScript;
  productName: string;
  productDescription?: string;
  productImageUrls: string[];
  avatarPersonaId?: string;
  avatarName?: string;
  avatarDescription?: string;
  planRunId?: string;
  /** Clone-mode only — full persona authored by Agent Moodboard. Mirrors
   *  C7 in docs/planning/clone-moodboard-agent-refactor.md. */
  avatarPersona?: UgcAvatarPersona;
  /** Clone-mode only — caption treatment replicated from the source ad. */
  captionSpec?: CaptionSpec;
  /** Clone-mode only — opening / between-beat / closing transition design. */
  transitionSpec?: TransitionSpec;
  /** Clone-mode only — text/hook overlay plan grounded in the source ad. */
  overlayPlan?: OverlayPlanItem[];
  /** Clone-mode only — the `ads:clone-source` artifact this plan was
   *  composed from. */
  cloneSourceArtifactId?: string;
  /** Clone-mode only — research artifact lineage for moodboard history. */
  researchBriefId?: string;
}

const EMPTY_MAP: Readonly<Record<number, UgcVideoPlan>> = Object.freeze({});

interface ProductLike {
  id: string;
  title: string;
}

export interface UseElementPlansOptions {
  /** Background refetch cadence in ms. Passes straight through to
   *  `useArtifacts`. The clone-moodboard review step opts in while the
   *  modal is in `moodboard_review` phase because `moodboard_compose_video`
   *  saves these plan artifacts mid-phase (1-3 min after the modal opens),
   *  long after the initial mount fetch — without polling, the cards never
   *  build until the user happens to switch windows. */
  refetchInterval?: number | false;
  /** Clone-mode scope: only include plans from this active plan run. */
  planRunId?: string | null;
  /** Clone-mode scope: only include plans composed from these clone-source artifacts. */
  cloneSourceArtifactIds?: readonly string[];
}

/**
 * Returns the latest non-deprecated `ugc-video-plan` artifacts for this
 * (workspace, product), keyed by `metadata.ugcVideoPlan.videoIndex`.
 *
 * Returns an empty (stable, frozen) record when workspace or product is
 * missing — safe to call unconditionally from render.
 */
export function useElementPlansForProduct(
  workspaceId: string | null | undefined,
  product: ProductLike | null | undefined,
  options?: UseElementPlansOptions,
): Record<number, UgcVideoPlan> {
  const { artifacts } = useArtifacts(
    workspaceId
      ? { workspace: workspaceId, subject: "ugc-video-plan" }
      : undefined,
    options?.refetchInterval !== undefined
      ? { refetchInterval: options.refetchInterval }
      : undefined,
  );

  return useMemo(() => {
    if (!workspaceId || !product) return EMPTY_MAP;
    const out: Record<number, UgcVideoPlan> = {};
    for (const a of artifacts) {
      if (
        !artifactMatchesProduct(a, product, { allowLegacyNameFallback: true })
      ) {
        continue;
      }
      const plan = readPlan(a);
      if (!plan) continue;
      if (options?.planRunId && plan.planRunId !== options.planRunId) continue;
      if (
        options?.cloneSourceArtifactIds?.length &&
        (!plan.cloneSourceArtifactId ||
          !options.cloneSourceArtifactIds.includes(plan.cloneSourceArtifactId))
      ) {
        continue;
      }
      // useArtifacts orders rows by -created_at, so the first row we see for a
      // given videoIndex is the latest; skip if we already filled this slot.
      if (out[plan.videoIndex] !== undefined) continue;
      out[plan.videoIndex] = plan;
    }
    return out;
  }, [artifacts, workspaceId, product, options?.planRunId, options?.cloneSourceArtifactIds]);
}

function readPlan(a: ArtifactRecord): UgcVideoPlan | null {
  const meta = a.metadata as Record<string, unknown> | null | undefined;
  if (!meta) return null;
  const payload = meta.ugcVideoPlan as Record<string, unknown> | undefined;
  if (!payload) return null;
  if (typeof payload.videoIndex !== "number") return null;
  // The backend writes the full `UgcVideoPlanRecord` shape into this slot
  // (see internal_views.ugc_video_plan_save). Trust the writer — the matcher
  // already gated on subject + productId.
  return payload as unknown as UgcVideoPlan;
}
